import { css, FlattenSimpleInterpolation, Theme } from 'styled-components';
import { IThemePropertyGetter, IThemeValueGetter } from '../types';

export const borderRadius =
  (size: keyof Theme['borderRadii'] = 'base'): IThemeValueGetter =>
  ({ theme }) =>
    theme.borderRadii[size] || theme.borderRadii.base;
export const boxShadow: IThemePropertyGetter<keyof Theme['boxShadows']> =
  (size = 'base') =>
  ({ theme }) =>
    theme.boxShadows[size] || theme.boxShadows.base;

/**
 * Rounds a card to the brand's radius. Only co-branded partner themes carry a
 * `card.borderRadius` value (via the integratedCX translation layer) — the
 * default is '' ("no opinion"), which emits no rule at all so stock corners
 * survive untouched. A brand's value is applied verbatim, including an
 * explicit '0' (truly square). The child rules keep Bootstrap's
 * card-header/footer inner corners in step.
 */
export const brandCardRadius = css`
  ${({ theme }: { theme: Theme }) =>
    theme.card?.borderRadius &&
    css`
      border-radius: ${theme.card.borderRadius};

      > .card-header:first-child {
        border-radius: ${theme.card.borderRadius} ${theme.card.borderRadius} 0 0;
      }

      > .card-footer:last-child {
        border-radius: 0 0 ${theme.card.borderRadius} ${theme.card.borderRadius};
      }
    `}
`;

export const clearFix = css`
  &::after {
    display: table;
    clear: both;
    content: '';
  }
`;

export const flipForRTL = ({ $rtl }: { $rtl: boolean }): FlattenSimpleInterpolation =>
  $rtl
    ? css`
        transform: scaleX(-1);
      `
    : css``;

export const flipFlexForRTL = ({ $rtl }: { $rtl: boolean }): FlattenSimpleInterpolation =>
  $rtl
    ? css`
        flex-direction: row-reverse;
      `
    : css`
        flex-direction: row;
      `;

// This shouldn't be needed, but inset-inline properties are not supported by safari
export const getRightValue =
  (val: string) =>
  ({ $rtl }: { $rtl: boolean }): FlattenSimpleInterpolation =>
    $rtl
      ? css`
          left: ${val};
        `
      : css`
          right: ${val};
        `;

export const getLeftValue =
  (val: string) =>
  ({ $rtl = false }: { $rtl?: boolean }): FlattenSimpleInterpolation =>
    $rtl
      ? css`
          right: ${val};
        `
      : css`
          left: ${val};
        `;

export const getMarginLeft =
  (val: string) =>
  ({ $rtl = false }: { $rtl?: boolean }): FlattenSimpleInterpolation =>
    $rtl
      ? css`
          margin-right: ${val};
          margin-left: 0;
        `
      : css`
          margin-left: ${val};
        `;

export const avoidBreakInside = css`
  break-inside: avoid;
  &::after {
    display: table;
    clear: both;
    visibility: hidden;
    content: '';
  }
`;
